import { useEffect, useRef } from 'react'

import type { WithdrawalFlowCallbacks } from '../flow/withdrawalFlowConfig'
import { logger } from '../logging/logger'
import type { DepositAddressOrHash } from '../withdrawal/useCryptoWithdrawalExecution'
import { useDepositAddressWithdrawalStatus } from '../withdrawal/useDepositAddressWithdrawalStatus'
import { useWithdrawalStatus } from '../withdrawal/useWithdrawalStatus'
import {
  WithdrawCompleteScreen,
  type WithdrawalStatus,
} from './WithdrawCompleteScreen'

/**
 * Follows a submitted crypto withdrawal to a terminal state. Split from the
 * screen so the polling query unmounts with it, and so the host is told exactly
 * once, when the chain settles rather than when we submitted.
 *
 * Both trackers are called on every render and the unused one idles on
 * `undefined` — a hook cannot be called conditionally, and the rail is fixed for
 * the life of this screen anyway.
 *
 * The callbacks are fired from an effect, where a throw would unmount the tree —
 * they arrive guarded (`guardHostCallbacks`), which is what makes that safe.
 *
 * Closing the flow ends the follow with it — the SDK runs no work behind a shut
 * sheet. The host still holds what `onWithdrawalSubmitted` gave it at submit and
 * can settle the transfer itself; the log below is how often that happens.
 */
export function WithdrawCryptoComplete({
  depositAddressOrHash,
  amountLabel,
  usdLabel,
  tokenLabel,
  callbacks,
  onDone,
}: {
  depositAddressOrHash: DepositAddressOrHash
  amountLabel: string
  /** What arrived, frozen at submission. */
  usdLabel: string | undefined
  tokenLabel: string | undefined
  callbacks: WithdrawalFlowCallbacks
  onDone: () => void
}) {
  const isVenueSubmitted = depositAddressOrHash.kind === 'depositAddress'
  const byTransaction = useWithdrawalStatus(
    depositAddressOrHash.kind === 'transaction'
      ? depositAddressOrHash.txHash
      : undefined,
  )
  const byDepositAddress = useDepositAddressWithdrawalStatus(
    depositAddressOrHash.kind === 'depositAddress'
      ? depositAddressOrHash.depositAddress
      : undefined,
  )
  const { isCompleted, isFailed, isRefunded } = isVenueSubmitted
    ? byDepositAddress
    : byTransaction
  // Completion is always established from a transaction, whichever rail ran —
  // ours on the relay path, the one recording the arrival on the venue path.
  const txHash = isVenueSubmitted
    ? byDepositAddress.txHash
    : depositAddressOrHash.txHash
  const notifiedRef = useRef(false)

  useEffect(() => {
    if (notifiedRef.current) {
      return
    }
    if (isCompleted) {
      notifiedRef.current = true
      callbacks.onWithdrawalSuccess?.({ txHash })
      return
    }
    // A relay FAILURE or REFUND is not "still settling": the transfer left the
    // wallet but never reached the recipient, so the host hears about it.
    if (isFailed) {
      notifiedRef.current = true
      callbacks.onWithdrawalError?.({
        message: isRefunded
          ? 'Withdrawal was refunded'
          : 'Withdrawal failed on-chain',
      })
    }
  }, [isCompleted, isFailed, isRefunded, callbacks, txHash])

  useEffect(
    () => () => {
      if (notifiedRef.current) {
        return
      }
      // The venue rail settles minutes after "requested", so closing first is
      // the expected path.
      if (isVenueSubmitted) {
        logger.info('crypto-withdrawal:closed_before_arrival', {
          depositAddressOrHash,
        })
        return
      }
      logger.warn('connect-rn:crypto-withdrawal:outcome_unreported', {
        depositAddressOrHash,
      })
    },
    [depositAddressOrHash, isVenueSubmitted],
  )

  return (
    <WithdrawCompleteScreen
      status={resolveStatus({
        isCompleted,
        isFailed,
        isRefunded,
        isVenueSubmitted,
      })}
      amountLabel={amountLabel}
      usdLabel={usdLabel}
      tokenLabel={tokenLabel}
      onDone={onDone}
    />
  )
}

/**
 * A venue-submitted withdrawal reads as done the moment the venue takes it: the
 * rest is out of the user's hands, and nothing of ours is on-chain to watch. It
 * still upgrades to `complete` if the funds land while the sheet is open.
 */
function resolveStatus({
  isCompleted,
  isFailed,
  isRefunded,
  isVenueSubmitted,
}: {
  isCompleted: boolean
  isFailed: boolean
  isRefunded: boolean
  isVenueSubmitted: boolean
}): WithdrawalStatus {
  if (isRefunded) {
    return 'refunded'
  }
  if (isFailed) {
    return 'failed'
  }
  if (isCompleted) {
    return 'complete'
  }
  return isVenueSubmitted ? 'requested' : 'settling'
}
